import {
  createDarkTheme,
  createLightTheme,
  type BrandVariants,
  type Theme,
} from '@fluentui/react-components';

import {
  DEFAULT_PALETTE,
  DEFAULT_SURFACE_STOP,
  resolvePalette,
  type ResolvedPalette,
} from './palettes';
import { AA_CONTRAST, BRAND_STOPS, contrastRatio, type BrandStop } from './ramp';

/**
 * Montserrat is the Bedrock typeface. Self-hosted via
 * `@fontsource-variable/montserrat` (imported in main.tsx) rather than loaded
 * from Google Fonts: self-hosting needs only `font-src 'self'` in a CSP, avoids
 * a render-blocking third-party request, and avoids handing client IP addresses
 * to a third party — which matters for a portal serving legal documents.
 *
 * Setting `fontFamilyBase` on the Theme is sufficient for every Fluent
 * component: FluentProvider's root class declares
 * `font-family: var(--fontFamilyBase)`, and everything beneath inherits it.
 *
 * `fontFamilyMonospace` is deliberately left alone — the checksum and storage
 * path columns want a monospace face.
 *
 * The typeface does not vary with the palette; only colour does.
 */
const fontFamilyBase =
  "'Montserrat Variable', Montserrat, 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif";

export type ResolvedTheme = 'light' | 'dark';

/** Step along the ramp, clamped at both ends. `+1` is one stop lighter. */
function stepStop(stop: BrandStop, steps: number): BrandStop {
  const index = BRAND_STOPS.indexOf(stop);
  const next = Math.min(BRAND_STOPS.length - 1, Math.max(0, index + steps));

  return BRAND_STOPS[next] ?? stop;
}

/**
 * The brand-filled surface: header, primary buttons, the active page number.
 *
 * Nothing is overridden when the palette resolves to Fluent's own stop 80 —
 * `createLightTheme` already maps it there, and re-stating it would only be a
 * chance to drift. The override exists for the Bedrock orange, which is pinned
 * to stop 120 for brand fidelity (`palettes.ts` holds that policy).
 *
 * ⚠ ACCEPTED ACCESSIBILITY EXCEPTION, for the orange palette only. Fluent puts
 * `colorNeutralForegroundOnBrand` (white) on all of these, and white on #FD991D
 * measures 2.15:1 — below WCAG AA (4.5:1) and below AA-large (3:1). It applies
 * to every primary action in the app, not only to chrome. To reverse it, set
 * `ORANGE_SURFACE_STOP` in `palettes.ts` back to 80 (#A85400), which gives white
 * text 5.34:1; nothing else needs to change. Every other palette clears AA by
 * construction, and `palettes.test.ts` holds that line.
 *
 * Hover and pressed step one and two stops down rather than taking Fluent's
 * defaults (stops 70 and 40): from a stop-120 base those jump several shades
 * darker and read as a different colour rather than as a state change.
 *
 * Only the FILLED-SURFACE tokens move. Brand foregrounds stay where
 * createLightTheme put them (stops 70–80), so brand text and links on neutral
 * backgrounds keep 5.34:1 and 7.17:1. `colorCompoundBrandBackground` — the
 * checkbox and radio fill — also stays at stop 80, because those are small
 * indicators where the checkmark's own contrast matters most.
 */
function brandSurface(ramp: BrandVariants, surfaceStop: BrandStop): Partial<Theme> {
  if (surfaceStop === DEFAULT_SURFACE_STOP) return {};

  return {
    colorBrandBackground: ramp[surfaceStop],
    colorBrandBackgroundHover: ramp[stepStop(surfaceStop, -1)],
    colorBrandBackgroundPressed: ramp[stepStop(surfaceStop, -2)],
    colorBrandBackgroundSelected: ramp[stepStop(surfaceStop, -1)],
  } satisfies Partial<Theme>;
}

/**
 * Selected-row and selected-nav background in dark mode.
 *
 * Fluent maps it to stop 20, which is around 1.1:1 against the dark surface —
 * effectively invisible, precisely when someone is confirming a multi-document
 * selection. No stop fixes that on its own: a tint dark enough to keep its own
 * text at AA can only ever be a whisper against #292929. So the tint is
 * deliberately NOT the indicator in dark mode:
 *   • the sidebar's active item is marked by a 3px colorCompoundBrandForeground1
 *     border, which is above 5:1 against the surface for every palette here —
 *     comfortably past the 3:1 WCAG 1.4.11 requires of a non-text UI indicator;
 *   • the grid uses selectionAppearance="neutral", so row selection shows as a
 *     neutral tint plus a checked checkbox rather than this token.
 *
 * What is left to decide is the warmest wash that still keeps the text *on* it
 * at AA, so this walks from stop 40 down until the selected-text token clears
 * 4.5:1. Orange lands on 40 (4.64:1, the value this app has always shipped);
 * the generated ramps are more chromatic at the dark end and land on 30.
 */
const TINT_CANDIDATES: readonly BrandStop[] = [40, 30, 20];

function pickDarkTintStop(ramp: BrandVariants, textColor: string): BrandStop {
  return TINT_CANDIDATES.find((stop) => contrastRatio(textColor, ramp[stop]) >= AA_CONTRAST) ?? 20;
}

// Keyed on the ResolvedPalette object, which `resolvePalette` caches and hands
// back by identity — so a re-render never rebuilds a theme it has already built.
const cache = new WeakMap<ResolvedPalette, Record<ResolvedTheme, Theme>>();

export function createThemes({ ramp, surfaceStop }: ResolvedPalette): Record<ResolvedTheme, Theme> {
  const surface = brandSurface(ramp, surfaceStop);

  const light: Theme = {
    ...createLightTheme(ramp),
    ...surface,
    fontFamilyBase,
  };

  const generatedDark = createDarkTheme(ramp);

  // Fluent's generated dark brand foregrounds are derived from the surface stop
  // it assumes (80). When the surface has been moved up the ramp they sit too
  // low to read, so they move with it — for orange that is stops 110 and 120,
  // giving 6.02:1 and 6.76:1. When the surface is at Fluent's own stop 80 its
  // defaults are already correct (about 5.2:1 and 6.0:1) and shifting them would
  // make things dramatically worse, so they are left alone.
  const darkForegrounds: Partial<Theme> =
    surfaceStop === DEFAULT_SURFACE_STOP
      ? {}
      : {
          colorBrandForeground1: ramp[stepStop(surfaceStop, -1)],
          colorBrandForeground2: ramp[surfaceStop],
        };

  const dark: Theme = {
    ...generatedDark,
    ...surface,
    ...darkForegrounds,
    fontFamilyBase,
    colorBrandBackground2:
      ramp[pickDarkTintStop(ramp, generatedDark.colorNeutralForeground2BrandSelected)],
  };

  return { light, dark };
}

export function themesFor(palette: ResolvedPalette): Record<ResolvedTheme, Theme> {
  const cached = cache.get(palette);
  if (cached) return cached;

  const built = createThemes(palette);
  cache.set(palette, built);

  return built;
}

/**
 * The Bedrock orange themes.
 *
 * Still exported because a few places need a theme before any user preference
 * exists — and because they are the baseline the palette tests compare against.
 */
export const themes = themesFor(resolvePalette(DEFAULT_PALETTE));
export const lightTheme = themes.light;
export const darkTheme = themes.dark;
